import React from 'react';
import { FiMoon, FiSun, FiCheck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useThemeStore } from '../../utils/store';
import { useT } from '../../utils/i18n';

function useSetTheme() {
  const setTheme = useThemeStore((s) => s.setTheme);
  const t = useT();
  return (theme) => setTheme(theme).catch(() => toast.error(t('theme.saveFailed')));
}

// Sun / moon button for the top ribbon and the sign-in pages.
export const ThemeToggle = ({ className = '' }) => {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useSetTheme();
  const t = useT();
  const dark = theme === 'dark';
  const label = dark ? t('theme.switchToLight') : t('theme.switchToDark');
  return (
    <button
      type="button"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
      className={`p-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition flex-shrink-0 ${className}`}
      title={label}
      aria-label={label}
      aria-pressed={dark}
    >
      {dark ? <FiSun size={18} /> : <FiMoon size={18} />}
    </button>
  );
};

// Light / Dark cards for Profile & Account.
export const AppearanceSettings = () => {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useSetTheme();
  const t = useT();
  const options = [
    { id: 'light', icon: FiSun, label: t('theme.light'), hint: t('theme.lightHint'), swatch: 'bg-[#ffffff] border-[#e5e7eb]', bar: 'bg-[#e5e7eb]' },
    { id: 'dark', icon: FiMoon, label: t('theme.dark'), hint: t('theme.darkHint'), swatch: 'bg-[#161b22] border-[#2c333f]', bar: 'bg-[#2c333f]' },
  ];
  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900">{t('theme.appearance')}</h2>
      <p className="text-sm text-gray-500 mb-4">{t('theme.appearanceHint')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label={t('theme.appearance')}>
        {options.map(({ id, icon: Icon, label, hint, swatch, bar }) => {
          const on = theme === id;
          return (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => !on && setTheme(id)}
              className={`text-left rounded-xl border-2 p-3 transition ${on ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              {/* A little picture of the look — fixed colours, same in both modes. */}
              <div className={`h-16 rounded-lg border mb-3 p-2 space-y-1.5 ${swatch}`}>
                <div className="h-2 w-1/2 rounded bg-[#fe6601]" />
                <div className={`h-2 w-3/4 rounded ${bar}`} />
                <div className={`h-2 w-2/3 rounded ${bar}`} />
              </div>
              <div className="flex items-center gap-2">
                <Icon size={16} className={on ? 'text-primary-600' : 'text-gray-500'} />
                <span className="font-medium text-gray-900">{label}</span>
                {on && <FiCheck size={16} className="ml-auto text-primary-600" />}
              </div>
              <p className="text-xs text-gray-500 mt-1">{hint}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
};
